import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChevronDown, ChevronRight, Copy, Check, ExternalLink, User, Bot, Wrench, MessageSquare, FileText, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import type { TraceItem } from '@/types/trace';

interface ContentSectionProps {
  title: string;
  content: unknown;
  defaultOpen?: boolean;
}

// Parse tool result content that has Title/URL/Text format
function parseToolResultContent(content: string): Array<{ title: string; url: string; text: string }> | null {
  const results: Array<{ title: string; url: string; text: string }> = [];
  const blocks = content.split(/(?=Title: )/);

  for (const block of blocks) {
    if (!block.trim()) continue;

    const titleMatch = block.match(/^Title: (.+?)(?:\n|$)/);
    const urlMatch = block.match(/URL: (https?:\/\/[^\s\n]+)/);
    const textMatch = block.match(/Text: ([\s\S]+?)(?=(?:Title: |$))/);

    if (titleMatch && urlMatch) {
      results.push({
        title: titleMatch[1].trim(),
        url: urlMatch[1].trim(),
        text: textMatch ? textMatch[1].trim().slice(0, 500) : '',
      });
    }
  }

  return results.length > 0 ? results : null;
}

function ContentSection({ title, content, defaultOpen = true }: ContentSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const { state } = useTraceSidebar();

  const handleCopy = async () => {
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatContent = () => {
    if (content === null || content === undefined) {
      return <span className="text-muted-foreground italic">No data</span>;
    }

    if (typeof content === 'string') {
      // Check if this looks like tool result with Title/URL/Text format
      if (state.contentFormat !== 'raw' && content.includes('Title:') && content.includes('URL:')) {
        const parsed = parseToolResultContent(content);
        if (parsed) {
          return (
            <div className="space-y-4">
              {parsed.map((result, idx) => (
                <div key={idx} className="p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-medium text-sm leading-tight">{result.title}</h4>
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-primary truncate block mb-2"
                  >
                    {result.url}
                  </a>
                  {result.text && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {result.text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          );
        }
      }

      if (state.contentFormat === 'raw') {
        return <pre className="whitespace-pre-wrap break-words text-sm bg-muted/50 p-4 rounded-lg overflow-auto">{content}</pre>;
      }

      // Try to parse as JSON for formatted display
      try {
        const parsed = JSON.parse(content);
        return (
          <pre className="whitespace-pre-wrap break-words text-sm bg-muted/50 p-4 rounded-lg overflow-auto">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      } catch {
        // Check if content looks like markdown (has headers, bold, lists, links, code, etc.)
        // This regex checks for markdown patterns ANYWHERE in the content, not just at line starts
        const looksLikeMarkdown =
          /^#{1,6}\s|^\*\*|^\- |\*\*.*\*\*|^\d+\.\s/m.test(content) ||  // Headers, bold at start, lists
          /\[.+?\]\(.+?\)/.test(content) ||  // Links: [text](url)
          /`.+?`/.test(content) ||  // Inline code: `code`
          /\*\*.+?\*\*/.test(content) ||  // Bold: **text**
          /\*.+?\*/.test(content);  // Italic: *text*
        if (looksLikeMarkdown) {
          return (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {children}
                    </a>
                  ),
                  h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-lg font-bold mt-4 mb-2">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-2">{children}</h3>,
                  h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1">{children}</h4>,
                  ul: ({ children }) => <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>,
                  p: ({ children }) => <p className="my-2">{children}</p>,
                  code: ({ children }) => <code className="bg-muted px-1 py-0.5 rounded text-sm">{children}</code>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-2">
                      {children}
                    </blockquote>
                  ),
                  hr: () => <hr className="my-4 border-muted" />,
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          );
        }
        return <pre className="whitespace-pre-wrap break-words text-sm bg-muted/50 p-4 rounded-lg overflow-auto">{content}</pre>;
      }
    }

    if (typeof content === 'object') {
      return (
        <pre className="whitespace-pre-wrap break-words text-sm bg-muted/50 p-4 rounded-lg overflow-auto">
          {JSON.stringify(content, null, 2)}
        </pre>
      );
    }

    return <span>{String(content)}</span>;
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg overflow-hidden">
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 bg-card">
            <div className="flex items-center gap-2">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="font-medium">{title}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-4 border-t max-h-[500px] overflow-auto">
            {formatContent()}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Helper to extract text from various content formats
function extractTextContent(content: unknown): string | null {
  if (!content) return null;

  // Handle array of content blocks like [{type: 'text', text: '...'}]
  if (Array.isArray(content)) {
    const texts = content
      .filter((item): item is { type: string; text: string } =>
        item && typeof item === 'object' && 'text' in item
      )
      .map(item => item.text);
    if (texts.length > 0) return texts.join('\n\n');
  }

  // Handle string content
  if (typeof content === 'string') {
    // Check for Python-style format: [{'type': 'text', 'text': '...', ...}]
    // The text value ends when we hit ', ' (another key) or '} (end of dict)
    if (content.startsWith("[{'type': 'text', 'text': '")) {
      const textStart = content.indexOf("'text': '") + "'text': '".length;
      // Find the end of the text value - look for ', ' (next key) or '} (dict end)
      const nextKeyPos = content.indexOf("', '", textStart);
      const dictEndPos = content.indexOf("'}", textStart);

      let textEnd = -1;
      if (nextKeyPos > 0 && dictEndPos > 0) {
        textEnd = Math.min(nextKeyPos, dictEndPos);
      } else if (nextKeyPos > 0) {
        textEnd = nextKeyPos;
      } else if (dictEndPos > 0) {
        textEnd = dictEndPos;
      }

      if (textStart > 0 && textEnd > textStart) {
        const extracted = content.substring(textStart, textEnd);
        // Unescape Python string escapes
        return extracted
          .replace(/\\n/g, '\n')
          .replace(/\\'/g, "'")
          .replace(/\\\\/g, '\\');
      }
    }

    // Try to parse as JSON if it looks like an array
    if (content.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
          const texts = parsed
            .filter((item): item is { type: string; text: string } =>
              item && typeof item === 'object' && 'text' in item
            )
            .map(item => item.text);
          if (texts.length > 0) return texts.join('\n\n');
        }
      } catch {
        // Not valid JSON, continue
      }
    }
    return content;
  }

  return null;
}

// Task-specific content display
interface TaskContentProps {
  item: TraceItem;
  formatToggle?: React.ReactNode;
}

function TaskContent({ item, formatToggle }: TaskContentProps) {
  const textOutput = extractTextContent(item.output);
  const textInput = typeof item.input === 'string' ? item.input : null;

  // Extract tools used from children (tool_call items)
  const toolsUsed = item.children
    .filter(child => child.type === 'tool_call' && child.toolName)
    .map(child => child.toolName as string);

  // Get unique tools
  const uniqueTools = [...new Set(toolsUsed)];

  // Get skills from metadata if available
  const skills = (item.metadata?.skills as string[] | undefined) || [];

  return (
    <div className="space-y-6">
      {/* Worker Info + Format Toggle - same row for vertical alignment */}
      <div className="flex items-center justify-between">
        {item.worker ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bot className="h-4 w-4" />
            <span>Executed by: <span className="font-medium text-foreground">{item.worker}</span></span>
          </div>
        ) : (
          <div />
        )}
        {formatToggle}
      </div>

      {/* System Prompt */}
      {item.systemPrompt && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">System Prompt</h3>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 max-h-64 overflow-y-auto">
            <pre className="text-sm whitespace-pre-wrap font-mono text-slate-700 dark:text-slate-300">{item.systemPrompt}</pre>
          </div>
        </div>
      )}

      {/* Task Query / Input */}
      {textInput && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Task Query</h3>
          </div>
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm">{textInput}</p>
          </div>
        </div>
      )}

      {/* Tools Used */}
      {uniqueTools.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Tools Used</h3>
            <Badge variant="secondary" className="text-xs">{uniqueTools.length}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {uniqueTools.map((tool, idx) => (
              <Badge key={idx} variant="outline" className="font-mono text-xs bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
                {tool}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Skills</h3>
            <Badge variant="secondary" className="text-xs">{skills.length}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Task Result */}
      {textOutput && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-emerald-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Result</h3>
          </div>
          <div className="bg-card border rounded-lg p-4 prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown
              components={{
                // Custom link rendering to open in new tab
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {children}
                  </a>
                ),
                // Style headers
                h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-lg font-bold mt-4 mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-2">{children}</h3>,
                h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1">{children}</h4>,
                // Style lists
                ul: ({ children }) => <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>,
                // Style paragraphs
                p: ({ children }) => <p className="my-2">{children}</p>,
                // Style code
                code: ({ children }) => <code className="bg-muted px-1 py-0.5 rounded text-sm">{children}</code>,
                // Style blockquotes
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-2">
                    {children}
                  </blockquote>
                ),
                // Horizontal rule
                hr: () => <hr className="my-4 border-muted" />,
              }}
            >
              {textOutput}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

// Setup-specific content display
interface SetupContentProps {
  input: unknown;
  output: unknown;
}

interface InputMessage {
  role: string;
  content: string;
}

interface ToolDefinition {
  name: string;
  description?: string;
  schema?: string;
}

function SetupContent({ input, output }: SetupContentProps) {
  const [expandedTools, setExpandedTools] = useState(false);

  // Parse input data
  const inputData = input as {
    system_prompt?: string;
    input_messages?: InputMessage[];
  } | null;

  // Parse output data
  const outputData = output as {
    available_tools?: ToolDefinition[];
    graph_config?: unknown;
  } | null;

  const systemPrompt = inputData?.system_prompt || '';
  const inputMessages = inputData?.input_messages || [];
  const availableTools = outputData?.available_tools || [];

  return (
    <div className="space-y-6">
      {/* System Prompt */}
      {systemPrompt && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">System Prompt</h3>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 border rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-foreground font-mono leading-relaxed">
              {systemPrompt}
            </pre>
          </div>
        </div>
      )}

      {/* Input Messages */}
      {inputMessages.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Input Messages</h3>
          </div>
          <div className="space-y-3">
            {inputMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === 'assistant' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  msg.role === 'user' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-emerald-100 dark:bg-emerald-900'
                }`}>
                  {msg.role === 'user' ? (
                    <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <Bot className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  )}
                </div>
                <div className={`flex-1 max-w-[85%] ${msg.role === 'assistant' ? 'text-right' : ''}`}>
                  <div className="text-xs text-muted-foreground mb-1 capitalize">{msg.role}</div>
                  <div className={`inline-block p-4 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800'
                      : 'bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800'
                  }`}>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <pre className="whitespace-pre-wrap text-sm font-sans m-0 bg-transparent p-0">
                        {msg.content}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Tools */}
      {availableTools.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Available Tools
              </h3>
              <Badge variant="secondary" className="text-xs">
                {availableTools.length}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedTools(!expandedTools)}
              className="text-xs h-7"
            >
              {expandedTools ? 'Collapse' : 'Expand'} All
            </Button>
          </div>
          <div className="grid gap-2">
            {availableTools.map((tool, idx) => (
              <div key={idx} className="border rounded-lg overflow-hidden bg-card">
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Wrench className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <span className="font-mono text-sm font-medium">{tool.name}</span>
                  </div>
                  {tool.description && (
                    <p className="text-sm text-muted-foreground mt-2 ml-7">{tool.description}</p>
                  )}
                </div>
                {tool.schema && expandedTools && (
                  <div className="px-4 py-3 border-t bg-muted/30">
                    <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-32">
                      {tool.schema}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback when no data available */}
      {!systemPrompt && inputMessages.length === 0 && availableTools.length === 0 && (
        <div className="text-center text-muted-foreground py-8 border rounded-lg bg-muted/30">
          <p className="text-sm">No setup configuration data available for this trajectory.</p>
        </div>
      )}
    </div>
  );
}

interface RunTabProps {
  item: TraceItem;
}

export function RunTab({ item }: RunTabProps) {
  const { state, setContentFormat } = useTraceSidebar();

  // Format toggle component
  const FormatToggle = () => (
    <div className="flex items-center justify-end">
      <Tabs
        value={state.contentFormat}
        onValueChange={(v) => setContentFormat(v as 'yaml' | 'raw' | 'json')}
      >
        <TabsList className="h-8">
          <TabsTrigger value="yaml" className="text-xs px-3 h-6">
            Formatted
          </TabsTrigger>
          <TabsTrigger value="raw" className="text-xs px-3 h-6">
            Raw
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );

  // Special handling for Setup item
  if (item.type === 'setup' && state.contentFormat !== 'raw') {
    return (
      <div className="space-y-6">
        <FormatToggle />
        <SetupContent input={item.input} output={item.output} />
      </div>
    );
  }

  // Special handling for Task item
  if (item.type === 'task' && state.contentFormat !== 'raw') {
    return (
      <TaskContent
        item={item}
        formatToggle={
          <Tabs
            value={state.contentFormat}
            onValueChange={(v) => setContentFormat(v as 'yaml' | 'raw' | 'json')}
          >
            <TabsList className="h-8">
              <TabsTrigger value="yaml" className="text-xs px-3 h-6">
                Formatted
              </TabsTrigger>
              <TabsTrigger value="raw" className="text-xs px-3 h-6">
                Raw
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />
    );
  }

  // Special handling for structured output observations
  if (item.type === 'observation' && item.observationType && state.contentFormat !== 'raw') {
    const isError = item.observationType === 'structured_output_error';
    const usedFallback = item.observationType === 'structured_output_fallback';

    return (
      <div className="space-y-6">
        {/* Format Toggle + Status Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {usedFallback && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                Fallback Extraction
              </Badge>
            )}
            {isError && (
              <Badge variant="destructive">
                Validation Error
              </Badge>
            )}
            {!usedFallback && !isError && (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                Validated
              </Badge>
            )}
          </div>
          <Tabs
            value={state.contentFormat}
            onValueChange={(v) => setContentFormat(v as 'yaml' | 'raw' | 'json')}
          >
            <TabsList className="h-8">
              <TabsTrigger value="yaml" className="text-xs px-3 h-6">
                Formatted
              </TabsTrigger>
              <TabsTrigger value="raw" className="text-xs px-3 h-6">
                Raw
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* AI Reasoning (if any text was produced before/alongside structured output) */}
        {item.reasoning && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-blue-500" />
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">AI Reasoning</h3>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm whitespace-pre-wrap">{item.reasoning}</p>
            </div>
          </div>
        )}

        {/* Message/Context */}
        {item.output && typeof item.output === 'string' && (
          <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
            {item.output}
          </div>
        )}

        {/* Structured Output Data */}
        {item.structuredOutput && (
          <ContentSection
            title="Structured Output"
            content={item.structuredOutput}
            defaultOpen={true}
          />
        )}
      </div>
    );
  }

  // Special handling for tool_call items to show tool name + args prominently
  if (item.type === 'tool_call' && state.contentFormat !== 'raw') {
    // Check if this is a structured output tool call (no actual tool execution)
    // These tools have the output IN the args, not in a separate result
    const isStructuredOutputTool = !item.output && !item.toolResult && item.toolArgs &&
      typeof item.toolArgs === 'object' && Object.keys(item.toolArgs).length > 0;

    return (
      <div className="space-y-6">
        {/* Format Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {item.isCacheHit && (
              <Badge variant="secondary">Cached</Badge>
            )}
            {isStructuredOutputTool && (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                Structured Output
              </Badge>
            )}
          </div>
          <Tabs
            value={state.contentFormat}
            onValueChange={(v) => setContentFormat(v as 'yaml' | 'raw' | 'json')}
          >
            <TabsList className="h-8">
              <TabsTrigger value="yaml" className="text-xs px-3 h-6">
                Formatted
              </TabsTrigger>
              <TabsTrigger value="raw" className="text-xs px-3 h-6">
                Raw
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* For structured output tools, show args as the output */}
        {isStructuredOutputTool ? (
          <ContentSection
            title="Structured Output"
            content={item.toolArgs}
            defaultOpen={true}
          />
        ) : (
          <>
            {/* Tool Call Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Tool Call</h3>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-mono font-medium text-amber-700 dark:text-amber-300">
                    {item.toolName || 'Unknown Tool'}
                  </span>
                </div>
                {item.toolArgs && (
                  <div className="bg-background/60 rounded p-3">
                    <div className="text-xs text-muted-foreground mb-1">Arguments</div>
                    <pre className="whitespace-pre-wrap break-words text-sm font-mono">
                      {typeof item.toolArgs === 'string'
                        ? item.toolArgs
                        : JSON.stringify(item.toolArgs, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* AI Reasoning (if present) */}
            {item.input && typeof item.input === 'string' && item.input !== JSON.stringify(item.toolArgs) && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-blue-500" />
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Reasoning</h3>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm whitespace-pre-wrap">{item.input}</p>
                </div>
              </div>
            )}

            {/* Tool Result Section */}
            {(item.output || item.toolResult) && (
              <ContentSection
                title="Result"
                content={item.output || item.toolResult}
                defaultOpen={true}
              />
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Format Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {item.isCacheHit && (
            <Badge variant="secondary">Cached</Badge>
          )}
        </div>
        <Tabs
          value={state.contentFormat}
          onValueChange={(v) => setContentFormat(v as 'yaml' | 'raw' | 'json')}
        >
          <TabsList className="h-8">
            <TabsTrigger value="yaml" className="text-xs px-3 h-6">
              Formatted
            </TabsTrigger>
            <TabsTrigger value="raw" className="text-xs px-3 h-6">
              Raw
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Input Section - only show if there's input data */}
      {(item.input !== undefined && item.input !== null || item.toolArgs) && (
        <ContentSection
          title="Input"
          content={item.input || item.toolArgs}
          defaultOpen={true}
        />
      )}

      {/* Output Section */}
      <ContentSection
        title="Output"
        content={item.output || item.toolResult}
        defaultOpen={true}
      />
    </div>
  );
}

export default RunTab;
